import { Injectable } from "@angular/core";

import * as Three from "three";
import {Intersection, Object3D, Ray, Vector2, Vector3} from "three";
import { InputMiddleware } from "../primitives/i-input-middlware";
import {TouchPrimitive} from "../primitives/touch-primitive";
import { ThreeService } from "../../rendering/threejs/three.service";
import { HasTouchPrimitive } from "../primitives/i-has-touch-primitive";
import { PointerTracker } from "../primitives/pointer-tracker";
import {PointerResult} from "../primitives/i-pointer-result";
import {config} from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ThreeInputService implements InputMiddleware {
  receiversByMesh: Map<string, TouchPrimitive> = new Map();
  meshIdByTrackerId: Map<string, string> = new Map();
  pointers: Map<string, Vector2> = new Map();

  constructor(public threeService: ThreeService) { }

  contextMenu(pointer: PointerTracker): boolean {
    const intersections = this.getMeshIntersections(pointer.pos);
    const receiverMeshIds = Array.from(this.receiversByMesh.keys());
    for (const intersection of intersections) {
      const impactMeshId = intersection.object.uuid;
      if (receiverMeshIds.includes(impactMeshId)) {
        const receiver = this.receiversByMesh.get(impactMeshId);
        const translatedCoords = receiver.__translateParentCoordToLocalCoord(
            intersection.uv,
        );
        pointer.relPos = translatedCoords;
        const result = receiver.contextMenu(pointer);
        if (result) {
          return true;
        }
      }
    }
    return false;
  }

  init() {
    return true;
  }

  addTouchPrimitiveOwnerToMeshId(id: string, owner: HasTouchPrimitive) {
    this.receiversByMesh.set(id, owner.getTouchPrimitive());
  }

  raycaster = new Three.Raycaster();

  getMeshIntersections(pos: Three.Vector2) {
    const pointer = new Three.Vector2();
    pointer.x = (pos.x / this.threeService.canvasWidth) * 2 - 1;
    pointer.y = -(pos.y / this.threeService.canvasHeight) * 2 + 1;
    this.raycaster.setFromCamera(pointer, this.threeService.__camera);
    const intersects = this.raycaster.intersectObjects(
      this.threeService.scene.children,
      true,
    );
    // this.raycaster.
    return intersects;
  }


  newEvent(config: {
    eventType: "pointerAdded" | "pointerChanged" | "pointerRemoved",
    pointer: PointerTracker,
    pointerOld: PointerTracker,
  }): PointerResult {
    let result: PointerResult = {
      consumed: false,
      consumedByChild: false,
      id: "ThreeInputMiddleware",
      children: [],
      cursorToPointer: false
    }
    if (config.eventType === "pointerAdded") {
      const ptResult = this.pointerAdded(config.pointer);
      result.children.push(ptResult)
      result.consumed = ptResult.consumed;
      result.consumedByChild = ptResult.consumed;
      result.cursorToPointer = ptResult.cursorToPointer;
    }
    if (config.eventType === "pointerChanged") {
      const ptResult = this.pointerChanged(config.pointerOld, config.pointer);
      result.children.push(ptResult)
      result.consumed = ptResult.consumed;
      result.consumedByChild = ptResult.consumed;
      result.cursorToPointer = ptResult.cursorToPointer;
    }
    if (config.eventType === "pointerRemoved") {
      const ptResult = this.pointerRemoved(config.pointer);
      result.children.push(ptResult)
      result.consumed = ptResult.consumed;
      result.consumedByChild = ptResult.consumed;
      result.cursorToPointer = ptResult.cursorToPointer;
    }
    return result
  }

  pointerAdded(pointer: PointerTracker): PointerResult {
    let result: PointerResult = {
      consumed: false,
      consumedByChild: false,
      id: "ThreeInputMiddleware",
      children: [],
      cursorToPointer: false
    }
    const intersections = this.getMeshIntersections(pointer.pos);
    this.pointers.set(pointer.id, pointer.pos);
    const receiverMeshIds = Array.from(this.receiversByMesh.keys());
    for (const childIntersection of intersections) {
      const childMeshId = childIntersection.object.uuid;
      if (receiverMeshIds.includes(childMeshId)) {
        const child = this.receiversByMesh.get(childMeshId);
        pointer.relPos = child.__translateParentCoordToLocalCoord(childIntersection.uv);
        const childResult = child.newEvent({
          eventType: "pointerAdded",
          pointer: pointer,
        })
        result.children.push(childResult)
        if (childResult.consumed) {
          this.meshIdByTrackerId.set(pointer.id, childMeshId);
          result.consumed = true;
          result.consumedByChild = true;
          return result;
        }
      }
    }
    return result;
  }

  intersections: Intersection[]
  meshHits: string[]
  allTouchPointers: string[]
  meshTrackingPointerId: string
  childTouchPrimitive: TouchPrimitive
  translatedCoords: Vector2
  hitMesh: Object3D
  hitMeshPosition: Vector3 = new Vector3()
  hitClosestPoint: Vector3 = new Vector3()
  hitray: Ray
  pointerResult: PointerResult
  translatedCoord2D: Vector2 = new Vector2()
  hitIntersection: Intersection
  isHit: boolean;
  isTracked: boolean;

  pointerChanged(
      pointerOld: PointerTracker,
      pointer: PointerTracker,
  ): PointerResult {
    let result: PointerResult = {
      consumed: false,
      consumedByChild: false,
      id: "ThreeInputMiddleware",
      children: [],
      cursorToPointer: false
    }
    this.pointers.set(pointer.id, pointer.pos);
    this.intersections = this.getMeshIntersections(pointer.pos);
    this.meshHits = this.intersections.map((hit) => hit.object.uuid);
    this.allTouchPointers = Array.from(this.receiversByMesh.keys());
    this.meshTrackingPointerId = this.meshIdByTrackerId.get(pointerOld?.id || null);
    for (const childMeshId of this.allTouchPointers) {
      this.childTouchPrimitive = this.receiversByMesh.get(childMeshId);
      this.isHit = this.meshHits.includes(childMeshId);
      this.isTracked = this.meshTrackingPointerId === childMeshId;
      this.translatedCoords = null;
      if (this.isHit) {
        this.hitIntersection = this.intersections.find(
            (hit) => hit.object.uuid === childMeshId,
        );
        this.translatedCoords = this.childTouchPrimitive.__translateParentCoordToLocalCoord( this.hitIntersection.uv, );
        // console.log("HIT Pointer changed: ", pointer.pos, this.translatedCoords)
      }
      if (!this.isHit) {
        this.translatedCoords = this.childTouchPrimitive.__translateParentCoordToLocalCoord(
            pointer.pos
        );
        // console.log("MISS Pointer changed: ", pointer.pos, this.translatedCoords)
      }
      pointer.relPos = this.translatedCoords;
      this.pointerResult = this.childTouchPrimitive.newEvent({
        eventType: "pointerChanged",
        pointer: pointer,
        intersectsMe: this.isHit || this.isTracked,
      })
      if (this.isTracked) {
        result.children.push(this.pointerResult)
        if (this.pointerResult.consumed) {
          result.consumed = true;
          result.consumedByChild = true;
        }
      }
      result.cursorToPointer = result.cursorToPointer || this.pointerResult.cursorToPointer;
    }
    return result
  }

  pointerRemoved(pointer: PointerTracker): PointerResult {
    let sunk = false;
    let result: PointerResult = {
      consumed: false,
      consumedByChild: false,
      id: "ThreeInputMiddleware",
      children: [],
      cursorToPointer: false
    }
    this.pointers.delete(pointer.id);
    const intersections = this.getMeshIntersections(pointer.pos);
    const receiverMeshIds = Array.from(this.receiversByMesh.keys());
    let visitedMeshIds = [];
    for (const intersection of intersections) {
      const impactMeshId = intersection.object.uuid;
      if (receiverMeshIds.includes(impactMeshId)) {
        const receiver = this.receiversByMesh.get(impactMeshId);
        visitedMeshIds.push(impactMeshId);
        const translatedCoords = receiver.__translateParentCoordToLocalCoord(
          intersection.uv,
        );
        pointer.relPos = translatedCoords;
        const ptResult = receiver.newEvent({
          eventType: "pointerRemoved",
          pointer: pointer,
        })
        sunk = sunk || ptResult.consumed;
        result.children.push(ptResult)
        if (sunk) {
          result.consumed = sunk;
          result.consumedByChild = sunk
          return result;
        }
      }
    }
    for (let key of receiverMeshIds) {
      if (visitedMeshIds.includes(key)) {
        continue;
      }
      const receiver = this.receiversByMesh.get(key);
      pointer.relPos = new Vector2(-50, -50); // Not found in mesh, assume off mesh
      // let result = await receiver.pointerRemoved(pointer, sunk);
      const ptResult = receiver.newEvent({
        eventType: "pointerRemoved",
        pointer: pointer,
      })
      sunk = sunk || ptResult.consumed;
      result.children.push(ptResult)
    }
    result.consumed = sunk;
    result.consumedByChild = sunk
    return result;
  }

  scroll(event: WheelEvent): boolean {
    // for (const meshId of this.receiversByMesh.keys()) {
    //   const receiver = this.receiversByMesh.get(meshId)
    //     const result = await receiver.scroll(event)
    //     if (result) {
    //       return true
    //     }
    //   }
    return false;
  }
}
